import { useState } from 'react';
import { Button } from 'components/ui/button';
import type { IncidentFilters, SensorScope } from './contracts';
import { unifiedAlertsService, useUnifiedIncidents } from './useUnifiedAlerts';
import type { UnifiedAlertsService } from './useUnifiedAlerts';
import { alertDate, ErrorNotice, Field, incidentStatusLabels, LoadingNotice } from './shared';
import IncidentDrawer from './IncidentDrawer';

export default function IncidentOverview({
  scope,
  service = unifiedAlertsService,
}: {
  scope?: SensorScope;
  service?: UnifiedAlertsService;
}) {
  const [mode, setMode] = useState<'' | 'live' | 'test'>('live');
  const [status, setStatus] = useState<IncidentFilters['status']>();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const filters: IncidentFilters = {
    ...scope,
    ...(mode ? { mode } : {}),
    ...(status ? { status } : {}),
  };
  const { query, incidents, evaluatedAt } = useUnifiedIncidents(filters, service);
  return (
    <section className="ua-stack" aria-label="Hændelser">
      <div className="ua-filters">
        <Field label="Driftstilstand">
          <select value={mode} onChange={(event) => setMode(event.target.value as typeof mode)}>
            <option value="live">LIVE</option>
            <option value="test">Test</option>
            <option value="">LIVE og Test</option>
          </select>
        </Field>
        <Field label="Hændelsens status">
          <select
            value={status || ''}
            onChange={(event) =>
              setStatus(
                event.target.value ? (event.target.value as IncidentFilters['status']) : undefined
              )
            }
          >
            <option value="">Alle statusser</option>
            {Object.entries(incidentStatusLabels).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Button
          type="button"
          variant="outline"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          {query.isFetching && !query.isFetchingNextPage ? 'Opdaterer…' : 'Opdatér'}
        </Button>
      </div>
      <p className="ua-muted">
        Gentagne observationer samles, indtil hændelsen afsluttes. Seneste vurdering:{' '}
        {alertDate(evaluatedAt)}.
      </p>
      {query.isPending && <LoadingNotice />}
      <ErrorNotice
        error={query.isError ? query.error : null}
        onRetry={() => void query.refetch()}
      />
      {query.isError && incidents.length > 0 && (
        <p className="ua-notice ua-warning">
          Viser tidligere indlæste hændelser. Listen kan være ufuldstændig.
        </p>
      )}
      {incidents.length > 0 && (
        <div className="ua-table-wrap">
          <table className="ua-table">
            <thead>
              <tr>
                <th>Hændelse / enhed</th>
                <th>Status</th>
                <th>Første / seneste observation</th>
                <th>Observationer</th>
                <th>Drift</th>
              </tr>
            </thead>
            <tbody>
              {incidents.map((incident) => (
                <tr key={incident.id}>
                  <td>
                    <button
                      type="button"
                      className="ua-link"
                      onClick={() => setSelectedId(incident.id)}
                    >
                      {incident.ruleTitle}
                    </button>
                    <div>{incident.sensorTitle}</div>
                    <small className="ua-muted">{incident.orgTitle}</small>
                  </td>
                  <td>
                    <div className="ua-stack" style={{ gap: 5 }}>
                      <span className={`ua-pill ${incident.status}`}>
                        {incidentStatusLabels[incident.status]}
                      </span>
                      <span className={`ua-pill ${incident.severity}`}>
                        {incident.severity === 'critical' ? 'Kritisk' : 'Advarsel'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div>{alertDate(incident.openedAt)}</div>
                    <small className="ua-muted">Senest: {alertDate(incident.lastObservedAt)}</small>
                  </td>
                  <td>{incident.occurrenceCount}</td>
                  <td>{incident.mode === 'live' ? 'LIVE' : 'Test'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!query.isPending && !query.isError && incidents.length === 0 && (
        <p className="ua-empty">Ingen hændelser matcher de valgte filtre.</p>
      )}
      {incidents.length > 0 && (
        <p className="ua-muted">
          {incidents.length} hændelser indlæst{query.hasNextPage ? ' · der findes flere' : ''}.
        </p>
      )}
      {query.hasNextPage && (
        <div>
          <Button
            type="button"
            variant="outline"
            disabled={query.isFetching}
            onClick={() => void query.fetchNextPage()}
          >
            {query.isFetchingNextPage ? 'Henter flere…' : 'Hent flere hændelser'}
          </Button>
        </div>
      )}
      {selectedId && (
        <IncidentDrawer
          key={selectedId}
          incidentId={selectedId}
          service={service}
          onClose={() => setSelectedId(null)}
        />
      )}
    </section>
  );
}
